import { create } from 'zustand';
import { apiService } from '../services/api';
import { API_ENDPOINTS } from '../config/api';

import { getErrorMessage } from '../services/errorHandler';
import { safeParseDate } from '../utils/dateUtils';

interface Location {
  latitude: number;
  longitude: number;
  address?: string;
}

interface Ride {
  id: string;
  ride_number: number;
  rider_id: string;
  driver_id?: string;
  service_id?: string;
  vehicle_type_id?: string;
  status: string;
  start_point: Location;
  end_point: Location;
  locations?: Location[];
  estimated_fare?: number;
  ride_fare?: number;
  payment_method?: string;
  payment_status?: string;
  driver?: any;
  rider?: any;
  created_at: string;
  started_at?: string;
  completed_at?: string;
  cancelled_at?: string;
  origin_address?: string;
  destination_address?: string;
  currency_symbol?: string;
}

interface RideState {
  currentRide: Ride | null;
  rideHistory: Ride[];
  isLoading: boolean;
  acceptRide: (rideId: string) => Promise<Ride>;
  rejectRide: (rideId: string, reason?: string) => Promise<void>;
  startRide: (rideId: string, distanceInMeters?: number, durationInSeconds?: number) => Promise<Ride>;
  completeRide: (rideId: string, finalFare?: number, distanceInMeters?: number, durationInSeconds?: number) => Promise<void>;
  arriveRide: (rideId: string, distanceToPickupInMeters?: number, etaToPickupInSeconds?: number) => Promise<Ride>;
  updateCurrentRide: (ride: Ride | null) => void;
  getDriverActiveRide: () => Promise<Ride | null>;
  getDriverRideHistory: () => Promise<Ride[]>;
}


const transformBackendRideToFrontend = (backendRide: any): Ride => {
  if (!backendRide) return null as any;

  return {
    id: backendRide.id?.toString() || backendRide.id,
    ride_number: backendRide.rideNumber || backendRide.ride_number || 0,
    rider_id: backendRide.riderId?.toString() || backendRide.rider_id?.toString() || '',
    driver_id: backendRide.driverId?.toString() || backendRide.driver_id?.toString(),
    service_id: backendRide.serviceId?.toString() || backendRide.service_id?.toString(),
    vehicle_type_id: backendRide.vehicleTypeId?.toString() || backendRide.vehicle_type_id?.toString(),
    status: backendRide.status || backendRide.ride_status_config?.name || backendRide.rideStatusConfig?.name || backendRide.ride_status_id?.toString() || 'PENDING',
    start_point: backendRide.startPoint || {
      latitude: backendRide.originLatitude || 0,
      longitude: backendRide.originLongitude || 0,
      address: backendRide.originAddress || backendRide.origin_address,
    },
    end_point: backendRide.endPoint || {
      latitude: backendRide.destinationLatitude || 0,
      longitude: backendRide.destinationLongitude || 0,
      address: backendRide.destinationAddress || backendRide.destination_address,
    },
    locations: backendRide.locations || [
      {
        latitude: backendRide.originLatitude || backendRide.startPoint?.latitude || 0,
        longitude: backendRide.originLongitude || backendRide.startPoint?.longitude || 0,
        address: backendRide.originAddress || backendRide.origin_address,
      },
      {
        latitude: backendRide.destinationLatitude || backendRide.endPoint?.latitude || 0,
        longitude: backendRide.destinationLongitude || backendRide.endPoint?.longitude || 0,
        address: backendRide.destinationAddress || backendRide.destination_address,
      },
    ],
    estimated_fare: backendRide.estimatedFare || backendRide.estimated_fare,
    ride_fare: backendRide.rideFare || backendRide.ride_fare || backendRide.finalFare,
    payment_method: backendRide.paymentMethod || backendRide.payment_method,
    payment_status: backendRide.paymentStatus || backendRide.payment_status,
    driver: backendRide.driver,
    rider: backendRide.rider,
    created_at: safeParseDate(backendRide.createdAt || backendRide.created_at) || '',
    started_at: safeParseDate(backendRide.startedAt || backendRide.started_at),
    completed_at: safeParseDate(backendRide.completedAt || backendRide.completed_at),
    cancelled_at: safeParseDate(backendRide.cancelledAt || backendRide.cancelled_at),
    origin_address: backendRide.originAddress || backendRide.origin_address,
    destination_address: backendRide.destinationAddress || backendRide.destination_address,
    currency_symbol: backendRide.currencySymbol || backendRide.currency_symbol || '$',
  };
};

export const useRideStore = create<RideState>((set: any) => ({
  currentRide: null,
  rideHistory: [],
  isLoading: false,

  getDriverActiveRide: async () => {
    try {
      const ride: any = await apiService.get<any>('/rides/driver/active');
      console.log('🔍 Backend response from /rides/driver/active:', ride);
      if (ride) {
        const transformedRide = transformBackendRideToFrontend(ride);
        console.log('✅ Transformed ride:', {
          id: transformedRide.id,
          ride_number: transformedRide.ride_number,
          status: transformedRide.status,
        });
        set({ currentRide: transformedRide });
        return transformedRide;
      }
      console.log('⚠️ No ride returned from backend');
      set({ currentRide: null });
      return null;
    } catch (error: any) {

      if (error?.response?.status === 404) {
        console.log('ℹ️ 404 - No active ride found (this is normal)');
        set({ currentRide: null });
        return null;
      }

      console.error('❌ Failed to get driver active ride:', error);
      set({ currentRide: null });
      return null;
    }
  },

  getDriverRideHistory: async () => {
    set({ isLoading: true });
    try {
      const history: any = await apiService.get<any>('/rides/driver/my-rides');
      const ridesList = Array.isArray(history) ? history : (history as any).data || [];
      const transformedRides = ridesList.map((ride: any) => transformBackendRideToFrontend(ride));
      set({ rideHistory: transformedRides });
      return transformedRides;
    } catch (error) {
      return [];
    } finally {
      set({ isLoading: false });
    }
  },

  acceptRide: async (rideId: string) => {
    try {
      const ride: any = await apiService.post<any>(
        API_ENDPOINTS.RIDES.ACCEPT.replace(':id', rideId),
      );
      const transformedRide = transformBackendRideToFrontend(ride);
      set({ currentRide: transformedRide });
      return transformedRide;
    } catch (error: any) {
      throw new Error(error.message || 'Failed to accept ride');
    }
  },

  rejectRide: async (rideId: string, reason?: string) => {
    try {
      await apiService.post<any>(
        API_ENDPOINTS.RIDES.REJECT.replace(':id', rideId),
        { reason: reason || 'Driver rejected' },
      );
    } catch (error: any) {
      throw new Error(getErrorMessage(error) || 'Failed to reject ride');
    }
  },

  startRide: async (
    rideId: string,
    distanceInMeters?: number,
    durationInSeconds?: number
  ) => {
    try {
      const requestBody: any = {};


      if (distanceInMeters !== undefined && distanceInMeters > 0) {
        requestBody.distanceInMeters = Math.round(distanceInMeters);
      }
      if (durationInSeconds !== undefined && durationInSeconds > 0) {
        requestBody.durationInSeconds = Math.round(durationInSeconds);
      }

      const ride: any = await apiService.post<any>(
        API_ENDPOINTS.RIDES.START.replace(':id', rideId),
        Object.keys(requestBody).length > 0 ? requestBody : undefined
      );
      const transformedRide = transformBackendRideToFrontend(ride);
      set({ currentRide: transformedRide });
      return transformedRide;
    } catch (error: any) {
      throw new Error(error.message || 'Failed to start ride');
    }
  },

  completeRide: async (
    rideId: string, 
    finalFare?: number,
    distanceInMeters?: number,
    durationInSeconds?: number
  ) => {
    try {

      if (!finalFare || typeof finalFare !== 'number' || finalFare <= 0 || isNaN(finalFare)) {
        throw new Error('finalFare must be a valid number greater than 0');
      }

      const requestBody: any = {
        finalFare: Number(finalFare), 
      };


      if (distanceInMeters !== undefined && distanceInMeters > 0) {
        requestBody.distanceInMeters = Math.round(distanceInMeters);
      }
      if (durationInSeconds !== undefined && durationInSeconds > 0) {
        requestBody.durationInSeconds = Math.round(durationInSeconds);
      }

      const response: any = await apiService.post(
        API_ENDPOINTS.RIDES.COMPLETE.replace(':id', rideId),
        requestBody
      );

      if (response && response.id) {
        const transformedRide = transformBackendRideToFrontend(response);
        set({ currentRide: transformedRide });
      } else {
        set({ currentRide: null });
      }
    } catch (error: any) {
      throw new Error(getErrorMessage(error) || 'Failed to complete ride');
    }
  },

  arriveRide: async (
    rideId: string,
    distanceToPickupInMeters?: number,
    etaToPickupInSeconds?: number
  ) => {
    try {
      const requestBody: any = {};


      if (distanceToPickupInMeters !== undefined && distanceToPickupInMeters > 0) {
        requestBody.distanceToPickupInMeters = Math.round(distanceToPickupInMeters);
      }
      if (etaToPickupInSeconds !== undefined && etaToPickupInSeconds > 0) {
        requestBody.etaToPickupInSeconds = Math.round(etaToPickupInSeconds);
      }

      const ride: any = await apiService.post<any>(
        API_ENDPOINTS.RIDES.ARRIVE.replace(':id', rideId),
        Object.keys(requestBody).length > 0 ? requestBody : undefined
      );
      const transformedRide = transformBackendRideToFrontend(ride);
      set({ currentRide: transformedRide });
      return transformedRide;
    } catch (error: any) {
      throw new Error(getErrorMessage(error) || 'Failed to mark arrival');
    }
  },

  updateCurrentRide: (ride: Ride | null) => {
    if (ride) {
      const transformedRide = transformBackendRideToFrontend(ride);
      set({ currentRide: transformedRide });
    } else {
      set({ currentRide: null });
    }
  },
}));

